import { getSessionId } from "@/analytics";
import { api } from "@/api";
import { getChatApi } from "@/chatApi";
import { storage } from "@/utils";
import { FormSubmitRequest, FormSubmitResponse } from "@/types";

/**
 * Generate a unique idempotency key for form submissions.
 * A new key should be generated for each user-initiated submission attempt.
 */
export function generateIdempotencyKey(): string {
  return `${Date.now()}-${crypto.randomUUID()}`;
}

/**
 * Submit a form in form-only mode via the dedicated /forms/submit endpoint.
 *
 * This endpoint is specifically for form-only mode submissions and should NOT
 * be used for forms within the chat context (those go through /message).
 *
 * @param siteId - TOMIS site slug
 * @param formKey - The form key to submit (e.g., "handoff_sms")
 * @param values - Form field values keyed by field id
 * @param idempotencyKey - Unique key to prevent duplicate submissions from network retries.
 * @returns Promise resolving to form submission response or error
 */
export async function submitFormOnly(
  siteId: string,
  formKey: string,
  values: Record<string, any>,
  idempotencyKey: string,
): Promise<{
  data?: FormSubmitResponse;
  error?: string;
}> {
  const userId = storage.getUserId(siteId);
  const conversationId = storage.getConversationId(siteId);

  if (!userId) {
    return {
      error: "User ID not found. Please try refreshing the page.",
    };
  }

  if (!formKey || typeof formKey !== "string") {
    return {
      error: "Form key is required.",
    };
  }

  if (!idempotencyKey || typeof idempotencyKey !== "string") {
    return {
      error: "Idempotency key is required for form submissions.",
    };
  }

  const requestBody: FormSubmitRequest = {
    user_id: userId,
    form_key: formKey,
    idempotency_key: idempotencyKey,
    values: values || {},
    page_url: window.location.href,
    test_mode: getChatApi().chat.test_mode || false,
  };

  const sessionId = getSessionId();
  if (sessionId) {
    requestBody.session_id = sessionId;
  }

  // Add conversation_id if we have an existing conversation
  if (conversationId) {
    requestBody.conversation_id = conversationId;
  }

  const { data, error } = await api.post<FormSubmitResponse>(
    `/${siteId}/chat-widget/forms/submit/`,
    requestBody,
  );

  if (error || !data) {
    return {
      error: error || "Failed to submit form",
    };
  }

  // Update conversation ID in storage (form submission may create a conversation)
  storage.setConversationId(data.conversation_id, siteId);

  return { data };
}
